/**
 * OutputPageThumbnails.js
 *
 * Left sidebar showing page thumbnails.
 * Click to navigate, drag to reorder pages.
 */

import React, { useState } from 'react';
import { COLORS, TYPOGRAPHY, SPACING, RADIUS } from '../designSystem';

export default function OutputPageThumbnails({
  pages,
  currentPageNum,
  onSelectPage,
  onReorderPages,
  onDeletePage,
}) {
  const [draggedPageId, setDraggedPageId] = useState(null);
  const [dragOverPageId, setDragOverPageId] = useState(null);

  const handleDragStart = (e, pageId) => {
    setDraggedPageId(pageId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, pageId) => {
    setDragOverPageId(pageId);
  };

  const handleDragLeave = () => {
    setDragOverPageId(null);
  };

  const handleDrop = (e, targetPageId) => {
    e.preventDefault();
    setDragOverPageId(null);

    if (!draggedPageId || draggedPageId === targetPageId) {
      setDraggedPageId(null);
      return;
    }

    // Reorder pages
    const draggedIndex = pages.findIndex(p => p.page_id === draggedPageId);
    const targetIndex = pages.findIndex(p => p.page_id === targetPageId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedPageId(null);
      return;
    }

    const newOrder = [...pages];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, pages[draggedIndex]);

    onReorderPages(newOrder.map(p => p.page_id));
    setDraggedPageId(null);
  };

  return (
    <div style={styles.container}>
      <h3 style={TYPOGRAPHY.H5}>Pages</h3>
      <div style={styles.list}>
        {pages.map((page) => (
          <div
            key={page.page_id}
            draggable
            onDragStart={(e) => handleDragStart(e, page.page_id)}
            onDragOver={handleDragOver}
            onDragEnter={(e) => handleDragEnter(e, page.page_id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, page.page_id)}
            style={{
              ...styles.thumbnail,
              backgroundColor:
                currentPageNum === page.page_num
                  ? COLORS.PRIMARY
                  : dragOverPageId === page.page_id
                  ? COLORS.BORDER_LIGHT
                  : COLORS.BG_SURFACE_SECONDARY,
              color: currentPageNum === page.page_num ? 'white' : COLORS.TEXT_PRIMARY,
              opacity: draggedPageId === page.page_id ? 0.5 : 1,
              border: dragOverPageId === page.page_id ? `2px solid ${COLORS.PRIMARY}` : undefined,
            }}
            onClick={() => onSelectPage(page.page_num)}
          >
            <div style={styles.pageNum}>Page {page.page_num}</div>
            <div
              style={styles.deleteBtn}
              onClick={(e) => {
                e.stopPropagation();
                onDeletePage(page.page_id);
              }}
            >
              ✕
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  container: {
    width: 120,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    borderRight: `1px solid ${COLORS.BORDER_DEFAULT}`,
    padding: SPACING.MD,
    overflowY: 'auto',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING.SM,
  },
  thumbnail: {
    padding: SPACING.SM,
    borderRadius: RADIUS.SM,
    cursor: 'pointer',
    border: `1px solid ${COLORS.BORDER_DEFAULT}`,
    position: 'relative',
  },
  pageNum: {
    fontSize: TYPOGRAPHY.CAPTION.fontSize,
    fontWeight: 'bold',
  },
  deleteBtn: {
    position: 'absolute',
    top: 4,
    right: 4,
    fontSize: '12px',
    cursor: 'pointer',
    opacity: 0.6,
  },
};
